import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { Modal, Row, Col, Form, Button, Dropdown, Spinner } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FaTimes,
  FaShareAlt,
  FaSyncAlt,
  FaThLarge,
  FaSearch,
  FaBell,
  FaBars,
  FaCalendarAlt,
  FaPaperPlane,
  FaRegCircle,
  FaListUl,
  FaLayerGroup,
  FaUserFriends,
  FaPaperclip,
  FaChevronRight,
  FaFileAlt,
  FaTrash,
  FaCloudUploadAlt,
  FaGoogleDrive,
  FaDropbox,
  FaBox,
  FaCloud,
  FaBold,
  FaItalic,
  FaUnderline,
  FaQuoteRight,
  FaAt,
  FaRegSmile,
  FaVideo,
  FaMicrophone,
  FaPlus,
  FaChevronDown,
  FaTasks,
} from "react-icons/fa";
import { toast } from "sonner";
import {
  GetRequest,
  PostRequest,
  PutRequest,
} from "../../Utils/hooks/ApiHandler";
import {
  formatDateInAlphabeticalMonth,
  formatTime,
  getUpdatedFields,
} from "../../Utils/helpers/commonhelper";
import { date } from "zod/v4";

const STATUS_OPTIONS = [
  { value: "complete", label: "Complete", color: "#1bbf6b" },
  { value: "progress", label: "In Progress", color: "#f1c232" },
  { value: "pending", label: "Pending", color: "#87909e" },
  { value: "onhold", label: "On Hold", color: "#f2994a" },
  { value: "cancel", label: "Cancel", color: "#e5484d" },
];
const STATUS_ACTION_OPTIONS = [
  { value: "complete", label: "COMPLETE", color: "#1bbf6b" },
  { value: "progress", label: "IN PROGRESS", color: "#f1c232" },
  { value: "pending", label: "Pending", color: "#87909e" },
  { value: "onhold", label: "On Hold", color: "#f2994a" },
  { value: "cancel", label: "Cancel", color: "#e5484d" },
];

const STATUS_MODAL_COPY = {
  complete: {
    title: "Complete Task",
    message: "Are you sure you want to mark as complete this task?",
    needsReason: false,
  },
   pending: {
    title: "Pending Task",
    message: "Are you sure you want to mark this task as  Pending?",
  },
  progress: {
    title: "Progress Task",
    message: "Are you sure you want to mark this task as In-progress?",
  },
  onhold: {
    title: "On Hold Task",
    message: "Are you sure you want to mark this task as On-Hold?",
  },
  cancel: {
    title: "Close Task",
    message: "Are you sure you want to cancel this task?",
    needsReason: true,
    reasonPlaceholder: "Please provide a reason for cancellation",
  },
};

const parseOwnerId = (raw) => {
  let value = raw;
  let attempts = 0;
  while (typeof value === "string" && value.trim() && attempts < 5) {
    try {
      value = JSON.parse(value);
    } catch (e) {
      break;
    }
    attempts += 1;
  }
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((o) => ({ value: o.value ?? o.id, label: o.label ?? o.name ?? "" }));
  }
  return [];
};
const parseTags = (raw) => {
  let value = raw;
  let attempts = 0;
  while (typeof value === "string" && value.trim() && attempts < 5) {
    try {
      value = JSON.parse(value);
    } catch (e) {
      break;
    }
    attempts += 1;
  }
  if (Array.isArray(value)) {
    return value
      .filter((t) => t !== null && t !== undefined && t !== "")
      .map((t) =>
        typeof t === "object"
          ? {
              value: t.value ?? t.id ?? t.label ?? t.name,
              label: t.label ?? t.name ?? String(t.value ?? ""),
            }
          : { value: t, label: String(t) },
      );
  }
  return [];
};

const PRIORITY_OPTIONS = [
  { value: "urgent", label: "Urgent", color: "#f2394a" },
  { value: "high", label: "High", color: "#f2a93b" },
  { value: "normal", label: "Normal", color: "#4592f0" },
  { value: "low", label: "Low", color: "#8590a2" },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline", "link"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["clean"],
  ],
};

const EMOJI_QUICK_LIST = [
  "😀",
  "😂",
  "😍",
  "👍",
  "🙌",
  "🎉",
  "🔥",
  "✅",
  "❤️",
  "😢",
  "🤔",
  "🚀",
];

const optionShape = z.object({
  value: z.union([z.string(), z.number()]),
  label: z.string(),
});

const taskDetailSchema = z.object({
  task_name: z.string().min(1, "Task title is required"),
  description: z.string().optional().nullable(),
  task_status: z.string().min(1, "Status is required"),
  assignees: z.array(optionShape).optional(),
  stage: optionShape.nullable().optional(),
  priority: optionShape.nullable().optional(),
  tags: z.array(optionShape).optional(),
  start_date: z.string().optional().nullable(),
  due_date: z.string().optional().nullable(),
  attachments: z.any().optional(),
});

const commentSchema = z.object({
  comment: z.string().trim().min(1, "Write something before sending"),
});


const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: 13,
    padding: "0 4px",
    boxShadow: "none",
    border: state.isFocused ? "1px solid #2684FF" : "1px solid #d1d5db",
    "&:hover": {
      borderColor: "#60a5fa",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 4px",
  }),
  input: (base) => ({
    ...base,
    padding: "2px 0",
    margin: 0,
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: 13,
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: 13,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 34,
    padding: "0 3px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  option: (base) => ({
    ...base,
    fontSize: 13,
    padding: "6px 10px",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 200,
    overflowY: "auto",
    scrollBehavior: "smooth",
    "::-webkit-scrollbar": {
      width: "8px",
    },
    "::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "::-webkit-scrollbar-thumb": {
      background: "#c9c9c9",
      borderRadius: "4px",
    },
    "::-webkit-scrollbar-thumb:hover": {
      background: "#888",
    },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#3b82f6",
    color: "white",
    fontSize: 12,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "white",
    fontWeight: 500,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "white",
    ":hover": {
      backgroundColor: "#ef4444",
      color: "white",
    },
  }),
};

const tagSelectStyles = {
  ...selectStyles,
  control: (base, state) => ({
    ...base,
    minHeight: 30,
    background: "transparent",
    border: "none",
    borderBottom: state.isFocused
      ? "1px solid #4592f0"
      : "1px solid transparent",
    boxShadow: "none",
    cursor: "pointer",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 2px" }),
  multiValue: (base) => ({
    ...base,
    background: "#f1e9ff",
    borderRadius: 12,
    padding: "0 2px",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#3b82f6",
    fontSize: 12,
    fontWeight: 600,
  }),
  placeholder: (base) => ({ ...base, color: "#8a8f98", fontSize: 12 }),
};

const tagSelectComponents = { DropdownIndicator: () => null };

const Dot = ({ color }) => (
  <span
    style={{
      display: "inline-block",
      width: 8,
      height: 8,
      borderRadius: "50%",
      background: color,
      marginRight: 8,
    }}
  />
);

const formatStatusLabel = (opt) => (
  <div className="d-flex align-items-center">
    <Dot color={opt.color} />
    <span style={{ letterSpacing: 0.4, fontWeight: 600 }}>{opt.label}</span>
  </div>
);

const formatPriorityLabel = (opt) => (
  <div className="d-flex align-items-center">
    <span style={{ marginRight: 8, color: opt.color, fontSize: 12 }}>
      &#9873;
    </span>
    {opt.label}
  </div>
);

const isImageFile = (file) =>
  file?.type?.startsWith("image/") ||
  /\.(png|jpe?g|gif|webp|svg)$/i.test(file?.name || file?.file_name || "");

const toISO = (d) => {
  if (!d) return null;
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return null;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
    dt.getDate(),
  ).padStart(2, "0")}`;
};

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

const isSameDay = (a, b) =>
  a && b && new Date(a).toDateString() === new Date(b).toDateString();

const isToday = (d) => d && isSameDay(d, new Date());

const nextWeekday = (from, targetDow) => {
  const x = startOfDay(from);
  const diff = (targetDow + 7 - x.getDay()) % 7 || 7;
  return addDays(x, diff);
};

const buildMonthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

let closeAllTdPopovers = null;
const TdPopover = ({
  onClose,
  children,
  style,
  className = "",
  anchorRect,
}) => {
  const popoverRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  useLayoutEffect(() => {
    if (!anchorRect || !popoverRef.current) return;
    const popoverEl = popoverRef.current;
    const popoverWidth = popoverEl.offsetWidth;
    const popoverHeight = popoverEl.offsetHeight;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let top = anchorRect.bottom + 6;
    let left = anchorRect.left;
    if (top + popoverHeight > viewportHeight - 10) {
      top = anchorRect.top - popoverHeight - 6;
    }
    if (top < 10) top = 10;

    if (left + popoverWidth > viewportWidth - 10) {
      left = viewportWidth - popoverWidth - 10;
    }
    if (left < 10) left = 10;
    setPosition({ top, left });
  }, [anchorRect]);
  useEffect(() => {
    closeAllTdPopovers = onClose;
    const handleOutsideMouseDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", handleOutsideMouseDown, true);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideMouseDown, true);
      document.removeEventListener("keydown", handleEscape);
      if (closeAllTdPopovers === onClose) {
        closeAllTdPopovers = null;
      }
    };
  }, [onClose]);
  return (
    <>
      <div className="td-popover-backdrop" />
      <div
        ref={popoverRef}
        className={`td-popover ${className}`}
        style={{
          ...style,
          top: position.top,
          left: position.left,
          position: "fixed",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>
  );
};
const OptionPopover = ({ options, onSelect, onClose, anchorRect }) => (
  <TdPopover
    onClose={onClose}
    className="tl-option-popover"
    anchorRect={anchorRect}
  >
    {options.map((opt) => (
      <div
        key={opt.value}
        className="tl-popover-row"
        onClick={() => onSelect(opt)}
      >
        <span className="tl-dot" style={{ background: opt.color }} />
        <span className="fs-13">{opt.label}</span>
      </div>
    ))}
  </TdPopover>
);
const ChangeStatusModal = ({ status, submitting, onCancel, onSubmit,taskGrant }) => {
  const copy = STATUS_MODAL_COPY[status?.value] || STATUS_MODAL_COPY.complete;
  const [reason, setReason] = useState("");
  return (
    <div className="tl-status-modal-backdrop" onMouseDown={onCancel}>
      <div className="tl-status-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="tl-status-modal-header">
          <span>{copy.title}</span>
          <span className="tl-status-modal-close" onClick={onCancel}>
            <FaTimes size={14} />
          </span>
        </div>
        <div className="tl-status-modal-body">
          <p className="tl-status-modal-message">{copy.message}</p>
          {copy.needsReason && (
            <>
              <label className="tl-status-modal-label">Reason</label>
              <textarea
                autoFocus
                className="tl-status-modal-textarea"
                placeholder={copy.reasonPlaceholder}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </>
          )}
        </div>
        <div className="tl-status-modal-footer">
          <button
            className="tl-btn-cancel"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className="tl-status-modal-submit"
            onClick={() => onSubmit(copy.needsReason ? reason : undefined)}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};
const TdDatePopover = ({
  startDate,
  dueDate,
  activeField,
  onChange,
  taskGrant,
  onClose,
  anchorRect,
}) => {
  const [field, setField] = useState(activeField || "due_date");
  const seed = (field === "start_date" ? startDate : dueDate) || new Date();
  const [viewYear, setViewYear] = useState(new Date(seed).getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date(seed).getMonth());
  // Start date can't be after the due date, and due date can't be before the start date.
  const isDateDisabled = (d) => {
    if (!d) return false;
    if (field === "start_date" && dueDate) {
      return startOfDay(d) > startOfDay(dueDate);
    }
    if (field === "due_date" && startDate) {
      return startOfDay(d) < startOfDay(startDate);
    }
    return false;
  };
  const applyDate = (d) => {
    if (isDateDisabled(d)) return;
    onChange({ [field]: toISO(d) });
    onClose();
  };
  const quickOptions = [
    { label: "Today", get: () => new Date() },
    { label: "Tomorrow", get: () => addDays(new Date(), 1) },
    { label: "This weekend", get: () => nextWeekday(new Date(), 6) },
    { label: "Next week", get: () => nextWeekday(new Date(), 1) },
    {
      label: "Next weekend",
      get: () => addDays(nextWeekday(new Date(), 6), 7),
    },
    { label: "2 weeks", get: () => addDays(new Date(), 14) },
    { label: "4 weeks", get: () => addDays(new Date(), 28) },
  ];
  const grid = buildMonthGrid(viewYear, viewMonth);
  const selectedForField = field === "start_date" ? startDate : dueDate;
  const changeMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) {
      m = 11;
      y -= 1;
    }
    if (m > 11) {
      m = 0;
      y += 1;
    }
    setViewMonth(m);
    setViewYear(y);
  };
  return (
    <TdPopover
      onClose={onClose}
      className="td-date-popover"
      anchorRect={anchorRect}
    >
      <div className="td-date-tabs">
        <button
          type="button"
          className={`td-date-tab ${field === "start_date" ? "td-date-tab-active" : ""}`}
          onClick={() => setField("start_date")}
        >
          {startDate ? formatDateInAlphabeticalMonth(startDate) : "Start date"}
        </button>
        <span className="td-muted">→</span>
        <button
          type="button"
          className={`td-date-tab ${field === "due_date" ? "td-date-tab-active" : ""}`}
          onClick={() => setField("due_date")}
        >
          {dueDate ? formatDateInAlphabeticalMonth(dueDate) : "Due date"}
        </button>
      </div>
      <div className="td-date-body">
        <div className="td-date-quick">
          {quickOptions.map((q) => {
            const qDate = q.get();
            const disabled = isDateDisabled(qDate);
            return (
              <div
                key={q.label}
                className={`td-date-quick-row ${disabled ? "td-date-quick-row-disabled" : ""}`}
                onClick={() => !disabled && applyDate(qDate)}
              >
                <span>{q.label}</span>
                <span className="td-muted fs-11">
                  {toISO(qDate) === toISO(new Date())
                    ? "Today"
                    : qDate.toLocaleDateString(undefined, { weekday: "short" })}
                </span>
              </div>
            );
          })}
          <div className="td-date-quick-row td-date-recurring">
            <span>Set Recurring</span>
            <FaChevronRight size={10} />
          </div>
        </div>
        <div className="td-date-calendar">
          <div className="td-date-cal-header">
            <span className="fw-semibold fs-13">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <div className="d-flex gap-2">
              <span className="td-cal-nav" onClick={() => changeMonth(-1)}>
                ‹
              </span>
              <span className="td-cal-nav" onClick={() => changeMonth(1)}>
                ›
              </span>
            </div>
          </div>
          <div className="td-date-cal-grid td-date-cal-dow">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
              <span key={d}>{d}</span>
            ))}
          </div>
          <div className="td-date-cal-grid">
            {grid.map((d, i) => {
              const disabled = d && isDateDisabled(d);
              return (
                <span
                  key={i}
                  className={[
                    "td-cal-cell",
                    !d && "td-cal-cell-empty",
                    d && isToday(d) && "td-cal-cell-today",
                    d &&
                      selectedForField &&
                      isSameDay(d, selectedForField) &&
                      "td-cal-cell-selected",
                    disabled && "td-cal-cell-disabled",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => d && !disabled && applyDate(d)}
                >
                  {d ? d.getDate() : ""}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </TdPopover>
  );
};
const AVATAR_COLORS = [
  "#7B68EE",
  "#4592f0",
  "#1bbf6b",
  "#f1c232",
  "#f2a93b",
  "#e5484d",
  "#00b8d9",
  "#d6409f",
  "#36b37e",
  "#8590a2",
];
const getAvatarColor = (seed = "") => {
  const str = String(seed || "U");
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};
const getInitials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "U";
const UserAvatar = ({ name, image, id, size = 26 }) => {
  if (image) {
    return (
      <img
        src={image}
        alt={name || "User"}
        className="td-avatar-img"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="td-avatar"
      style={{
        width: 30,
        height: 30,
        // background: getAvatarColor(id || name),
        fontSize: size <= 22 ? 9 : 10,
      }}
    >
      {getInitials(name)}
    </div>
  );
};
const useOutsideClose = (onClose) => {
  const ref = useRef(null);
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handleMouseDown, true);
    return () =>
      document.removeEventListener("mousedown", handleMouseDown, true);
  }, [onClose]);
  return ref;
};
const TaskCommentEditor = ({ onSubmit, assigneeOptions = [],taskGrant }) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [showFormatBar, setShowFormatBar] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showMention, setShowMention] = useState(false);
  const [mentionedIds, setMentionedIds] = useState([]);
  const [files, setFiles] = useState([]);
  const [mentionAnchorRect, setMentionAnchorRect] = useState(null);
  const [emojiAnchorRect, setEmojiAnchorRect] = useState(null);
  const focusEditor = () => editorRef.current?.focus();
  const insertAtCursor = (text) => {
    focusEditor();
    document.execCommand("insertText", false, text);
    setIsEmpty(!editorRef.current?.textContent?.trim());
  };
  const runFormat = (cmd, val = null) => {
    focusEditor();
    document.execCommand(cmd, false, val);
  };
  const handleInput = () => setIsEmpty(!editorRef.current?.textContent?.trim());
  const handleSend = () => {
    if(!taskGrant?.isEdit) return toast.warning("You are not authorized for this action!")
    const html = editorRef.current?.innerHTML || "";
    const text = editorRef.current?.textContent || "";
    if (!text.trim()) return;
    onSubmit(html, files);
    editorRef.current.innerHTML = "";
    setIsEmpty(true);
    setFiles([]);
    setMentionedIds([]);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const handleFilesChosen = (e) => {
    setFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
    e.target.value = ""; 
  };
  return (
    <div className="td-comment-editor">
      {files.length > 0 && (
        <div className="td-comment-files">
          {files.map((f, i) => (
            <span key={i} className="td-comment-file-chip">
              <FaPaperclip size={10} className="me-1" />
              {f.name}
              <FaTimes
                size={9}
                className="ms-1"
                style={{ cursor: "pointer" }}
                onClick={() =>
                  setFiles((prev) => prev.filter((_, idx) => idx !== i))
                }
              />
            </span>
          ))}
        </div>
      )}
      {showFormatBar && (
        <div className="td-comment-format-bar">
          <span onClick={() => runFormat("bold")}>
            <FaBold size={11} />
          </span>
          <span onClick={() => runFormat("italic")}>
            <FaItalic size={11} />
          </span>
          <span onClick={() => runFormat("underline")}>
            <FaUnderline size={11} />
          </span>
          <span onClick={() => runFormat("insertUnorderedList")}>
            <FaListUl size={11} />
          </span>
          <span onClick={() => runFormat("formatBlock", "blockquote")}>
            <FaQuoteRight size={11} />
          </span>
        </div>
      )}
      <div
        ref={editorRef}
        className="td-comment-input"
        contentEditable
        data-placeholder="Write a comment..."
        onInput={handleInput}
        onKeyDown={handleKeyDown}
      />
      <div className="td-comment-toolbar">
        <span
          className="td-comment-tool"
          title="More"
          onClick={() => toast.info("More options coming soon")}
        >
          <FaPlus size={12} />
        </span>
        <span
          className="td-comment-tool td-comment-tool-label"
          onClick={() => setShowFormatBar((v) => !v)}
        >
          Comment <FaChevronDown size={9} />
        </span>
        <input
          type="file"
          multiple
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFilesChosen}
        />
        <span
          className="td-comment-tool"
          title="Attach"
          onClick={() => fileInputRef.current?.click()}
        >
          <FaPaperclip size={12} />
        </span>
        <span
          className="td-comment-tool"
          title="Mention"
          style={{ position: "relative" }}
          onClick={(e) => {
            setMentionAnchorRect(e.currentTarget.getBoundingClientRect());
            setShowMention((v) => !v);
          }}
        >
          <FaAt size={12} />
          {showMention && (
            <TdPopover
              onClose={() => setShowMention(false)}
              className="td-comment-mention-popover"
              anchorRect={mentionAnchorRect}
            >
              {assigneeOptions.filter((opt) => !mentionedIds.includes(opt.value)).length === 0 && (
                <div className="td-comment-mention-empty">No people found</div>
              )}
              {assigneeOptions
                .filter((opt) => !mentionedIds.includes(opt.value))
                .map((opt) => (
                  <div
                    key={opt.value}
                    className="td-comment-mention-row"
                    onClick={() => {
                      insertAtCursor(`@${opt.label} `);
                      setMentionedIds((prev) => [...prev, opt.value]);
                      setShowMention(false);
                    }}
                  >
                    <UserAvatar name={opt.label} id={opt.value} size={20} />
                    <span className="fs-13">{opt.label}</span>
                  </div>
                ))}
            </TdPopover>
          )}
        </span>
        <span
          className="td-comment-tool"
          title="Emoji"
          style={{ position: "relative" }}
          onClick={(e) => {
            setEmojiAnchorRect(e.currentTarget.getBoundingClientRect());
            setShowEmoji((v) => !v);
          }}
        >
          <FaRegSmile size={12} />
          {showEmoji && (
            <TdPopover
              onClose={() => setShowEmoji(false)}
              className="td-comment-emoji-popover"
              anchorRect={emojiAnchorRect}
            >
              {EMOJI_QUICK_LIST.map((e) => (
                <span
                  key={e}
                  className="td-comment-emoji-item"
                  onClick={() => {
                    insertAtCursor(e);
                    setShowEmoji(false);
                  }}
                >
                  {e}
                </span>
              ))}
            </TdPopover>
          )}
        </span>
        <span
          className="td-comment-tool"
          title="GIF"
          onClick={() => toast.info("GIF picker not wired up yet")}
        >
          <FaVideo size={12} />
        </span>
        <span
          className="td-comment-tool"
          title="Voice note"
          onClick={() => toast.info("Voice notes not wired up yet")}
        >
          <FaMicrophone size={12} />
        </span>
        <button
          type="button"
          className="td-comment-send"
          disabled={isEmpty}
          onClick={handleSend}
        >
          <FaPaperPlane size={11} />
        </button>
      </div>
    </div>
  );
};
export const TaskDetailModal = ({
  show,
  onClose,
  task_id,
  projectData,
  headers,
  taskGrant,
  extras,
  customerId,
  onUpdated,
}) => {
  const [saving, setSaving] = useState(false);
  const [task, setTask] = useState([]);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [assigneeOpts, setAssigneeOpts] = useState([]);
  const [stageOpts, setStageOpts] = useState([]);
  const [attachments, setAttachments] = useState([]); // File[] (new) + existing {id,file_name,url}
  const [attachmentsDirty, setAttachmentsDirty] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [currentStatus, setCurrentStatus] = useState(
    task?.task_status || "open",
  );
  const [statusAnchorRect, setStatusAnchorRect] = useState(null);
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false);
  const [statusAction, setStatusAction] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const defaultValues = useMemo(
    () => ({
      task_name: task?.task_name || "",
      description: task?.description || "",
      task_status: task?.task_status || "open",
      assignees: parseOwnerId(task?.owner_id),
      stage: (() => {
        if (!task?.project_stage_id) return null;
        // Prefer the label from stageOpts (always populated correctly).
        // Falling back to task.stage_name only if stageOpts hasn't loaded yet,
        // since some API responses (e.g. after save) omit stage_name entirely.
        const matched = stageOpts.find(
          (opt) => String(opt.value) === String(task.project_stage_id),
        );
        if (matched) return matched;
        return task?.stage_name
          ? { value: task.project_stage_id, label: task.stage_name }
          : null;
      })(),
      priority:
        PRIORITY_OPTIONS.find((p) => p.value === task?.priority) || null,
      tags: parseTags(task?.tags),
      start_date: task?.start_date || "",
      due_date: task?.end_date || "",
    }),
    [task, stageOpts],
  );
  const currentUser = useMemo(
    () => ({
      id: extras?.customer?.customer_details?.id,
      name:
        extras?.customer?.customer_details?.full_name ||
        extras?.customer?.customer_details?.name ||
        "You",
      image:
        extras?.customer?.customer_details?.profile_image ||
        extras?.customer?.customer_details?.avatar ||
        null,
    }),
    [extras],
  );
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty,isSubmitSuccessful },
  } = useForm({
    resolver: zodResolver(taskDetailSchema),
    defaultValues,
  });
  const [dateAnchorRect, setDateAnchorRect] = useState(null);
  const [dateFieldOpen, setDateFieldOpen] = useState(null); // "start_date" | "due_date" | null
  const [activityHistory, setActivityHistory] = useState([]);
  const openDatePopover = (fieldName) => (e) => {
    e.stopPropagation();
    setDateAnchorRect(e.currentTarget.getBoundingClientRect());
    setDateFieldOpen(fieldName);
  };
  const closeDatePopover = () => {
    setDateFieldOpen(null);
    setDateAnchorRect(null);
  };
  const titleRef = useRef(null);
  const autoResizeTitle = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  const [taskLoading, setTaskLoading] = useState(false);
  const fetchDetailsOfTaskForEdit = async () => {
    if (!task_id) return;
    setTaskLoading(true);
    try {
      const res = await GetRequest(
        `project/getDetailsOfTaskForEdit/${task_id}/${customerId}/${currentUser?.id}`,
        { headers },
        extras,
      );
      const taskData = res?.data;
      if (taskData) {
        setTask(taskData);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setTaskLoading(false);
    }
  };
  useEffect(() => {
    if (show && task_id) fetchDetailsOfTaskForEdit();
    if (!show) setTask(null);
  }, [show, task_id]);
  useEffect(() => {
    reset(defaultValues);
    setAttachments(task?.attachments || []);
    setAttachmentsDirty(false);
    setCurrentStatus(task?.task_status || "open");
    setTimeout(() => autoResizeTitle(titleRef.current), 0);
  }, [defaultValues, reset, task]);
  useEffect(() => {
       fetchComments();
  }, [show, task?.task_id,isSubmitSuccessful]);

  useEffect(() => {
    if (show) {
      fetchOwners();
      fetchStageWithProjectId();
    }
  }, [show, projectData?.id]);
  const fetchOwners = async () => {
    try {
      const res = await GetRequest(
        `users/users/fetchUserAsOwner/${customerId}`,
        { headers },
        extras,
      );
      if (res?.err) return;
      setAssigneeOpts(
        (res?.data || []).map((x) => ({ value: x.id, label: x.full_name })),
      );
    } catch (error) {
      console.error(error);
    }
  };
  const fetchStageWithProjectId = async () => {
    if (!projectData?.id) return;
    try {
      const res = await GetRequest(
        `project/fetchStages/${projectData.id}/${customerId}/${extras?.customer?.customer_details?.id}`,
        { headers },
        extras,
      );
      if (res?.status !== "success") return;
      setStageOpts(
        (res.data || []).map((d) => ({ value: d.id, label: d.stage_name })),
      );
    } catch (error) {
      console.error(error);
    }
  };
  const fetchComments = async () => {
    setCommentsLoading(true);
    try {
      const res = await GetRequest(
        `project/fetchTaskComment/${task_id}`,
        { headers },
        extras,
      );
      if (res?.status === "success") setComments(res.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setCommentsLoading(false);
    }
  };
  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setAttachments((prev) => [...prev, ...incoming]);
    setAttachmentsDirty(true);
  };
  const handleBrowseClick = () => fileInputRef.current?.click();
  const handleFileInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  };
  const removeAttachment = (idx) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
    setAttachmentsDirty(true);
  };
  const onSubmit = async (data) => {
    if(!taskGrant?.isEdit) return toast.warning("You are not authorized for this action!")
    setSaving(true);
    try {
      const parseIfNeeded = (data) => {
        if (typeof data === "string") {
          try {
            return JSON.parse(data);
          } catch (e) {
            return data;
          }
        }
        return data;
      };
      const payload = {
        task_id: task?.id,
        stage_id: data.stage?.value || task?.project_stage_id,
        project_id: projectData?.id,
        task_name: data.task_name,
        description: data.description,
        task_status: currentStatus,
        priority: data.priority?.value || null,
        owner_id: parseOwnerId(data.assignees),
        tags: (data.tags || []).map((t) => t.value),
        start_date: data.start_date || null,
        end_date: data.due_date || null,
        attachments: [],
        //attachments.filter((f) => f instanceof File),
      };
      const previousPayload = {
        task_id: task?.id,
        stage_id: task?.project_stage_id,
        project_id: task?.project_id,
        task_name: task?.task_name,
        description: task?.description,
        task_status: task?.task_status,
        priority: task?.priority,
        owner_id: parseIfNeeded(task?.owner_id),
        tags: task?.tags || [],
        start_date: task?.start_date || null,
        end_date: task?.end_date || null,
        attachments: [],
      };
      payload.updating_field = getUpdatedFields(previousPayload, payload);
      const res = await PutRequest(
        "project/updateStageTask",
        payload,
        { headers },
        extras,
      );
      if (!res || res.status === "fail") {
        return toast.error(res?.message || "Something went wrong");
      }
      toast.success(res.message || "Task updated successfully");
      onUpdated?.();
      await fetchDetailsOfTaskForEdit(); 
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong while saving the task");
    } finally {
      setSaving(false);
    }
  };
  const handleStatusClick = (e) => {
    if(!taskGrant?.isEdit) return toast.warning("You are not authorized for this action!")
    e.stopPropagation();
    setStatusAnchorRect(e.currentTarget.getBoundingClientRect());
    setStatusPopoverOpen(true);
  };
  const closeStatusPopover = () => {
    setStatusPopoverOpen(false);
    setStatusAnchorRect(null);
  };
  const handleStatusSubmit = async (reason) => {
    if (!statusAction) return;
    setStatusSubmitting(true);
    try {
      const res = await PutRequest(
        "project/changeTaskStatus",
        {
          task_id: task?.id,
          task_status: statusAction.value,
          ...(reason !== undefined ? { reason } : {}),
        },
        { headers },
        extras,
      );
      if (!res || res.status === "fail") {
        toast.error(res?.message || "Could not update task status");
        return;
      }
      setCurrentStatus(statusAction.value);
      toast.success(res.message || "Task status updated successfully");
      setStatusAction(null);
      onUpdated?.();
    } catch (error) {
      console.error(error);
      toast.error("Could not update task status");
    } finally {
      setStatusSubmitting(false);
    }
  };
  const fetchActivity = async () => {
    // if(task?.id) return
    try {
      const res = await GetRequest(
        `project/fetchSpecificTaskActivity/${task_id}/${currentUser?.id}/${customerId}`,
        { headers },
        extras,
      );
      if (res?.status !== "success" || res?.status === "fail") {
        return setActivityHistory([]);
      }
      setActivityHistory(res?.data);
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    fetchActivity();
  }, [show,isSubmitSuccessful]);
  const onCommentSubmit = async ({ comment, files = [] }) => {
    if (!comment?.trim()) return;
    const tempId = `temp-${Date.now()}`;
    const optimisticComment = {
      id: tempId,
      comment,
      user_name: currentUser.name,
      user_image: currentUser.image,
      user_login_id: currentUser.id,
      created_at: new Date().toISOString(),
      attachment_names: files.map((f) => f.name),
      _optimistic: true,
    };
    setComments((prev) => [...prev, optimisticComment]);
    try {
      const res = await PostRequest(
        "project/insertTaskComment",
        {
          task_id: task?.id,
          comment,
          // user_login_id: currentUser.id,
          // attachment: files,
        },
        files.length > 0,
        { headers },
        extras,
      );
      if (!res || res.status === "fail") {
        setComments((prev) => prev.filter((c) => c.id !== tempId));
        return toast.error(res?.message || "Could not post comment");
      }
      fetchComments();
    } catch (error) {
      console.error(error);
      setComments((prev) => prev.filter((c) => c.id !== tempId));
      toast.error("Could not post comment");
    }
  };
  const activityFeed = useMemo(() => {
    const items = [];
    (activityHistory || []).forEach((a, i) => {
      items.push({
        type: "activity",
        key: `activity-${i}`,
        creator_name: a.creator_name || a.update_name,
        description: a.description,
        created_at: a.created_at || a.updated_at,
      });
    });
    (task?.attachments || [])
      .filter((a) => !(a instanceof File)) // only already-uploaded files
      .forEach((a, i) => {
        items.push({
          type: "attachment",
          key: `attachment-${a.id || i}`,
          user_name: a.uploaded_by_name || currentUser.name,
          user_image: a.uploaded_by_image || null,
          created_at: a.created_at || task?.created_at,
          file: a,
        });
      });
    comments.forEach((c, i) => {
      items.push({ type: "comment", key: c.id || `comment-${i}`, ...c });
    });
    return items;
  }, [activityHistory, task, comments, currentUser]);
  const activityLogItems = useMemo(
    () => activityFeed.filter((item) => item.type !== "comment"),
    [activityFeed],
  );
  const commentItems = useMemo(
    () => activityFeed.filter((item) => item.type === "comment"),
    [activityFeed],
  );

  

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      dialogClassName="task-detail-dialog"
      contentClassName="task-detail-content"
    >
      {taskLoading || !task ? (
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: 400 }}
        >
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="d-flex flex-column h-100"
      >
        {/* ---------------- HEADER ---------------- */}
        <div className="td-header d-flex align-items-center justify-content-between px-3 py-2">
          <div className="d-flex align-items-center gap-2 fs-13 td-muted">
            <span>Team Space</span>
            <FaChevronRight size={9} />
            <span>{projectData?.project_name || "Project"}</span>
          </div>
          <div className="d-flex align-items-center gap-3">
            <span className="fs-12 td-muted">
              Created{" "}
              {task?.created_at
                ?`${formatDateInAlphabeticalMonth(task.created_at)}, ${formatTime(task.created_at)} `
                : ""}
            </span>
            <Button size="sm" variant="outline-light" className="td-share-btn">
              <FaShareAlt className="me-1" size={11} /> Share
            </Button>
            <FaSyncAlt className="td-icon-btn" />
            <FaThLarge className="td-icon-btn" />
            <FaTimes className="td-icon-btn" onClick={onClose} />
          </div>
        </div>

        {/* ---------------- BODY ---------------- */}
        <div className="td-body flex-grow-1">
          <Row className="g-0 h-100">
            {/* ======================= LEFT / MAIN ======================= */}

            <Col md={8} className="td-main px-4 py-3">
              {/* TITLE */}
              <div className="d-flex align-items-start">
                <span className="td-field-label">
                  <FaTasks className="me-2" /> Task Name
                </span>
                <Form.Group className="mb-2 flex-grow-1" controlId="task_name">
                  <Controller
                    name="task_name"
                    control={control}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        as="textarea"
                        // rows={1}
                        ref={(el) => {
                          field.ref(el);
                          titleRef.current = el;
                          if (el) autoResizeTitle(el);
                        }}
                        onInput={(e) => autoResizeTitle(e.target)}
                        className="td-title-input"
                        style={{minHeight:'100px'}}
                        placeholder="Task name"
                      />
                    )}
                  />
                  {errors.task_name && (
                    <div className="td-error">{errors.task_name.message}</div>
                  )}
                </Form.Group>
              </div>

              {/* FIELD GRID */}
              <div className="td-field-grid mb-3">
                {/* STATUS */}
                <div className="td-field-row">
                  <span className="td-field-label">
                    <FaRegCircle className="me-2" /> Status
                  </span>
                  <div
                    className="td-field-value"
                    style={{ position: "relative" }}
                  >
                    {(() => {
                      const opt =
                        STATUS_OPTIONS.find((s) => s.value === currentStatus) ||
                        STATUS_OPTIONS[0];
                      return (
                        <>
                          <span
                            className="tl-status-pill"
                            style={{ background: opt.color }}
                            onClick={handleStatusClick}
                          >
                            <span className="tl-status-pill-dot" />
                            {opt.label}
                          </span>
                          {statusPopoverOpen && (
                            <OptionPopover
                              options={STATUS_ACTION_OPTIONS}
                              onSelect={(opt2) => {
                                closeStatusPopover();
                                setStatusAction(opt2);
                              }}
                              onClose={closeStatusPopover}
                              anchorRect={statusAnchorRect}
                            />
                          )}
                          {statusAction && (
                            <ChangeStatusModal
                              status={statusAction}
                              taskGrant={taskGrant}
                              submitting={statusSubmitting}
                              onCancel={() => setStatusAction(null)}
                              onSubmit={handleStatusSubmit}
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
                {/* STAGE */}
                <div className="td-field-row">
                  <span className="td-field-label">
                    <FaLayerGroup className="me-2" /> Stage
                  </span>
                  <div className="td-field-value">
                    <Form.Group
                      controlId="stage"
                      style={{ width: 200, backgroundColor: "#fdfdfd" }}
                    >
                      <Controller
                        name="stage"
                        control={control}
                        render={({ field }) => (
                          <Select
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            placeholder="Select stage"
                            options={stageOpts}
                            value={field.value}
                            onChange={(opt) => field.onChange(opt)}
                          />
                        )}
                      />
                    </Form.Group>
                  </div>
                </div>

                {/* DATES */}
                <div className="td-field-row" style={{ position: "relative" }}>
                  <span className="td-field-label">
                    <FaCalendarAlt className="me-2" /> Dates
                  </span>
                  <div className="td-field-value d-flex align-items-center gap-2">
                    <Controller
                      name="start_date"
                      control={control}
                      render={({ field: startField }) => (
                        <Controller
                          name="due_date"
                          control={control}
                          render={({ field: dueField }) => (
                            <>
                              <span
                                className={`td-date-pill ${isToday(startField.value) ? "td-date-pill-today" : ""}`}
                                onClick={openDatePopover("start_date")}
                              >
                                <FaCalendarAlt size={10} className="me-1" />
                                {startField.value
                                  ? isToday(startField.value)
                                    ? "Today"
                                    : formatDateInAlphabeticalMonth(
                                        startField.value,
                                      )
                                  : "Start date"}
                              </span>
                              <span className="td-muted">→</span>
                              <span
                                className={`td-date-pill ${isToday(dueField.value) ? "td-date-pill-today" : ""}`}
                                onClick={openDatePopover("due_date")}
                              >
                                <FaCalendarAlt size={10} className="me-1" />
                                {dueField.value
                                  ? isToday(dueField.value)
                                    ? "Today"
                                    : formatDateInAlphabeticalMonth(
                                        dueField.value,
                                      )
                                  : "Due date"}
                              </span>

                              {dateFieldOpen && (
                                <TdDatePopover
                                  taskGrant={taskGrant}
                                  startDate={startField.value}
                                  dueDate={dueField.value}
                                  activeField={dateFieldOpen}
                                  onChange={(patch) => {
                                    if (patch.start_date !== undefined)
                                      startField.onChange(patch.start_date);
                                    if (patch.due_date !== undefined)
                                      dueField.onChange(patch.due_date);
                                  }}
                                  onClose={closeDatePopover}
                                  anchorRect={dateAnchorRect}
                                />
                              )}
                            </>
                          )}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* PRIORITY */}
                <div className="td-field-row">
                  <span className="td-field-label">
                    <FaListUl className="me-2" /> Priority
                  </span>
                  <div className="td-field-value">
                    <Form.Group controlId="priority" style={{ width: 200 }}>
                      <Controller
                        name="priority"
                        control={control}
                        render={({ field }) => (
                          <Select
                            styles={selectStyles}
                            isClearable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            placeholder="Empty"
                            options={PRIORITY_OPTIONS}
                            value={field.value}
                            formatOptionLabel={formatPriorityLabel}
                            onChange={(opt) => field.onChange(opt)}
                          />
                        )}
                      />
                    </Form.Group>
                  </div>
                </div>

                {/* TRACK TIME */}
                {/* <div className="td-field-row">
                  <span className="td-field-label">
                    <FaClock className="me-2" /> Track time
                  </span>
                  <div className="td-field-value">
                    <Button type="button" size="sm" className="td-track-btn">
                      ▶ Start
                    </Button>
                  </div>
                </div> */}
                {/* ASSIGNEES */}
                <div className="td-field-row">
                  <span className="td-field-label">
                    <FaUserFriends className="me-2" /> Assignees
                  </span>
                  <div className="td-field-value">
                    <Form.Group controlId="assignees" style={{ width: 350 }}>
                      <Controller
                        name="assignees"
                        control={control}
                        render={({ field }) => (
                          <Select
                            isMulti
                            styles={selectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            placeholder="Empty"
                            options={assigneeOpts}
                            value={field.value}
                            onChange={(opt) => field.onChange(opt || [])}
                          />
                        )}
                      />
                    </Form.Group>
                  </div>
                </div>
                {/* TAGS */}
                <div className="td-field-row">
                  <span className="td-field-label">
                    <FaPaperclip className="me-2" /> Tags
                  </span>
                  <div className="td-field-value td-tag-field">
                    <Form.Group controlId="tags" style={{ width: 220 }}>
                      <Controller
                        name="tags"
                        control={control}
                        render={({ field }) => (
                          <CreatableSelect
                            isMulti
                            styles={tagSelectStyles}
                            components={tagSelectComponents}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            placeholder="+ Add tag"
                            options={field.value}
                            value={field.value}
                            onChange={(opt) => field.onChange(opt || [])}
                          />
                        )}
                      />
                    </Form.Group>
                  </div>
                </div>
              </div>

              <hr className="td-divider" />

              {/* DESCRIPTION — ReactQuill */}
              <Form.Group className="mb-3" controlId="description">
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <ReactQuill
                      theme="snow"
                      value={field.value || ""}
                      onChange={(value) => field.onChange(value)}
                      placeholder="Add description"
                      style={{ minHeight: "200px" }}
                      className="td-quill"
                      modules={QUILL_MODULES}
                    />
                  )}
                />
              </Form.Group>

              {/* ATTACHMENTS */}
              <div className="mb-2 d-flex align-items-center justify-content-between">
                <span className="fw-semibold fs-13">
                  Attachments {attachments.length > 0 && attachments.length}
                </span>
              </div>

              <div
                className={`td-dropzone mb-3 ${isDragging ? "td-dropzone-active" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
              >
                Drop files here or{" "}
                <span className="td-browse-link" onClick={handleBrowseClick}>
                  browse
                </span>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={handleFileInputChange}
                />
              </div>

              {attachments.length > 0 && (
                <div className="td-attachment-grid mb-3">
                  {attachments.map((file, idx) => {
                    const isFileObj = file instanceof File;
                    const name = isFileObj ? file.name : file.file_name;
                    const previewUrl = isFileObj
                      ? URL.createObjectURL(file)
                      : file.url;
                    return (
                      <div className="td-attachment-card" key={idx}>
                        <button
                          type="button"
                          className="td-attachment-remove"
                          onClick={() => removeAttachment(idx)}
                        >
                          <FaTrash size={10} />
                        </button>
                        {isImageFile(file) ? (
                          <img
                            src={previewUrl}
                            alt={name}
                            className="td-attachment-thumb"
                          />
                        ) : (
                          <div className="td-attachment-thumb td-attachment-file-icon">
                            <FaFileAlt size={22} />
                          </div>
                        )}
                        <div className="td-attachment-name" title={name}>
                          {name}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* extra decorative action links */}
              <div className="td-action-links">
                {/* <div>+ Add fields</div>
                <div>⤴ Add subtask</div>
                <div>⇄ Relate items or add dependencies</div>
                <div>☑ Create checklist</div> */}
                <Dropdown drop="up">
                  <Dropdown.Toggle
                    as="div"
                    id="attach-file-toggle"
                    className="td-action-links-toggle"
                  >
                    📎 Attach file
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="td-upload-menu">
                    <Dropdown.Item onClick={handleBrowseClick}>
                      <FaCloudUploadAlt className="me-2" /> Upload file
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        toast.info("Hook this up to your Dropbox connector")
                      }
                    >
                      <FaDropbox className="me-2" /> Dropbox
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        toast.info("Hook this up to your OneDrive connector")
                      }
                    >
                      <FaCloud className="me-2" /> OneDrive/SharePoint
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        toast.info("Hook this up to your Box connector")
                      }
                    >
                      <FaBox className="me-2" /> Box
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        toast.info(
                          "Hook this up to your Google Drive connector",
                        )
                      }
                    >
                      <FaGoogleDrive className="me-2" /> Google Drive
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Col>

            {/* ======================= RIGHT / ACTIVITY ======================= */}
            <Col md={4} className="td-activity px-3 py-3">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="fw-semibold fs-14">Activity</span>

                <div className="d-flex gap-3 td-muted">
                  <FaSearch size={12} />
                  <FaBell size={12} />
                  <FaBars size={12} />
                </div>
              </div>

              <div className="td-comments-list mb-3">
                {commentsLoading && (
                  <div className="td-muted fs-12">Loading activity…</div>
                )}
                {!commentsLoading && activityFeed.length === 0 && (
                  <div className="td-muted fs-12">No activity yet.</div>
                )}

                {activityLogItems.map((item) => {
                  if (item.type === "activity") {
                    // no early-return on missing created_at anymore — always
                    // render the activity line; only the date badge is
                    // conditional, so rows without ANY timestamp still show
                    // the description instead of vanishing.
                    return (
                      <div className="td-activity-line" key={item.key}>
                        <span className="fs-12 td-muted">
                          <span className="td-activity-user">
                            {item.creator_name}
                          </span>{" "}
                          {item.description}
                        </span>
                        {item.created_at && (
                          <span className="td-muted fs-11 ms-2">
                           {formatDateInAlphabeticalMonth(item.created_at)}, {formatTime(item.created_at)}
                          </span>
                        )}
                      </div>
                    );
                  }
                  /* -------- "X uploaded a file" + thumbnail -------- */
                  if (item.type === "attachment") {
                    const file = item.file;
                    const fileUrl = file?.url || file?.file_url || "";
                    const fileName =
                      file?.file_name || file?.name || "Attachment";
                    return (
                      <div className="td-comment" key={item.key}>
                        <UserAvatar
                          name={item.user_name}
                          image={item.user_image}
                          id={item.user_name}
                        />
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="fs-12 td-muted">
                              <span className="td-activity-user">
                                {item.user_name}
                              </span>{" "}
                              uploaded a file
                            </span>
                            {item.created_at && (
                              <span className="td-muted fs-11">
                                {formatDateInAlphabeticalMonth(item.created_at)}, {formatTime(item.created_at)}
                              </span>
                            )}
                          </div>
                          <div className="td-activity-attachment">
                            {isImageFile(file) && fileUrl ? (
                              <img src={fileUrl} alt={fileName} />
                            ) : (
                              <div className="td-activity-attachment-file">
                                <FaFileAlt size={16} />
                              </div>
                            )}
                            <span className="fs-11 td-muted">{fileName}</span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
                {commentItems.length > 0 && (
                  <div
                    className="td-comments-section"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      marginTop: "12px",
                    }}
                  >
                    {commentItems.map((item) => (
                      <div
                        className="td-comment-box"
                        key={item.key || item.id}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          backgroundColor: "#f9fafb",
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        {/* Comment Header */}
                        <div className="d-flex align-items-center gap-2">
                          <UserAvatar
                            name={item.creator_name}
                            image={item.user_image}
                            id={item.created_by || item.user_name}
                            size={22}
                          />
                          <span
                            className="fw-semibold fs-13"
                            style={{ color: "#1f2937" }}
                          >
                            {item.creator_name || "User"}
                          </span>
                          <span
                            className="td-muted fs-11"
                            style={{ marginLeft: "auto", color: "#9ca3af" }}
                          >
                            {item.created_at
                              ? `${formatDateInAlphabeticalMonth(item.created_at)}, ${formatTime(item.created_at)}`
                              : "Just now"}
                          </span>
                          {item._optimistic && (
                            <span className="td-muted fs-11 fst-italic">
                              Sending…
                            </span>
                          )}
                        </div>

                        {/* Comment Body Text */}
                        <div
                          className="fs-13 td-comment-text"
                          style={{ color: "#374151", wordBreak: "break-word" }}
                          dangerouslySetInnerHTML={{
                            __html: item.description || item.comment || "",
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="td-comment-box">
                <TaskCommentEditor
                  taskGrant={taskGrant}
                  assigneeOptions={assigneeOpts}
                  onSubmit={(html, files) =>
                    onCommentSubmit({ comment: html, files })
                  }
                />
              </div>
            </Col>
          </Row>
        </div>

        {/* ---------------- FOOTER ---------------- */}
        <div className="td-footer d-flex justify-content-end gap-3 px-3 py-2">
          <button
            type="button"
            className="btn btn-default-outline"
            onClick={onClose}
          >
            Cancel
          </button>
          {(isDirty || attachmentsDirty) && (
            <button type="submit" className="btn btn-default" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
          )}
        </div>
      </Form>
      )}

      {/* ---------------- SCOPED DARK THEME CSS ---------------- */}
      <style>{`
        .task-detail-dialog {
          max-width: 96vw;
          width: 96vw;
          margin: 2vh auto;
          height: 96vh;
        }
        .task-detail-content {
          background: #ffffff;
          color: #1f2937;
          border-radius: 8px;
          border: 1px solid #e8e9ec;
          height: 96vh;
          overflow: hidden;
        }
        .td-header, .td-footer { border-bottom: 1px solid #e8e9ec; flex-shrink: 0; }
        .td-footer { border-top: 1px solid #e8e9ec; border-bottom: none; background: #ffffff; }
        .td-body { overflow: hidden; }
        .td-body .row.h-100 { height: 100%; }
        .td-main { height: 100%; overflow-y: auto; }
        .td-muted { color: #6b7280; }
        .td-icon-btn { color: #6b7280; cursor: pointer; font-size: 14px; }
        .td-icon-btn:hover { color: #111827; }
        .td-share-btn { font-size: 12px; border-color: #e0e2e7; color: #1f2937; }
        .td-type-pill {
          display: inline-flex;
          align-items: center;
          font-size: 12px;
          color: #6b7280;
          background: #f4f5f7;
          border: 1px solid #e0e2e7;
          border-radius: 4px;
          padding: 2px 8px;
        }
        .td-type-dot {
          width: 8px; height: 8px; border-radius: 50%;
          background: #4592f0; display: inline-block; margin-right: 6px;
        }
        .td-title-input {
          background: transparent; border: none; color: #111827;
          font-size: 22px; font-weight: 700; padding: 4px 0;
          width: 100%; resize: none; overflow: hidden;
          white-space: pre-wrap; word-break: break-word; line-height: 1.3;
        }
        .td-title-input:focus { background: #f4f5f7; box-shadow: none; color: #111827; }
        .td-ai-banner {
          background: #f4f5f7; border-radius: 6px; padding: 10px 12px;
          font-size: 13px; color: #6b7280;
        }
        .td-field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 24px; }
        .td-field-row { display: flex; align-items: center; }
        .td-field-label {
          width: 110px; flex-shrink: 0; color: #6b7280; font-size: 13px;
          display: flex; align-items: center;
        }
        .td-field-value { flex: 1; }
        .td-tag-field { display: flex; align-items: center; min-height: 34px; }
        .td-date-input {
          background: #f4f5f7; border: 1px solid #e0e2e7; color: #1f2937;
          font-size: 13px; width: 140px;
        }

        /* ---- date pills + start/due date popover ---- */
        .td-date-pill {
          display: inline-flex; align-items: center; font-size: 13px; color: #1f2937;
          cursor: pointer; padding: 5px 10px; border-radius: 4px; border: 1px solid #e0e2e7;
          background: #f4f5f7; transition: background 0.2s, border-color 0.2s; white-space: nowrap;
        }
        .td-date-pill:hover { background: #eef1f5; border-color: #c7cbd1; }
        .td-date-pill-today { color: #b8860b; font-weight: 600; }

        .td-popover-backdrop { position: fixed; inset: 0; z-index: 1049; background: rgba(0,0,0,0.05); }
        .td-popover {
          position: fixed; z-index: 1050; background: #ffffff; border: 1px solid #e0e2e7;
          border-radius: 8px; box-shadow: 0 8px 32px rgba(0,0,0,0.18); min-width: 220px;
          padding: 6px; animation: tdPopoverIn 0.15s ease; max-height: 80vh; overflow-y: auto;
        }
        @keyframes tdPopoverIn {
          from { opacity: 0; transform: scale(0.95) translateY(-4px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }

        .td-date-popover { width: 560px; padding: 0; overflow: hidden; }
        .td-date-tabs {
          display: flex; align-items: center; gap: 8px; padding: 10px 14px;
          border-bottom: 1px solid #e0e2e7; background: #f8f9fb;
        }
        .td-date-tab {
          background: #ffffff; border: 1px solid #e0e2e7; color: #4b5563;
          border-radius: 6px; font-size: 12px; padding: 6px 14px; cursor: pointer; transition: all 0.2s;
        }
        .td-date-tab:hover { border-color: #c7cbd1; }
        .td-date-tab-active { border-color: #4592f0; color: #111827; background: #eaf2ff; }
        .td-date-body { display: flex; }
        .td-date-quick { width: 160px; border-right: 1px solid #e0e2e7; padding: 6px; }
        .td-date-quick-row {
          display: flex; justify-content: space-between; padding: 7px 10px; border-radius: 6px;
          cursor: pointer; font-size: 13px; color: #1f2937; transition: background 0.15s;
        }
        .td-date-quick-row:hover { background: #eef1f5; }
        .td-date-quick-row-disabled {
          opacity: 0.4;
          cursor: not-allowed;
          pointer-events: none;
        }
        .td-date-recurring { border-top: 1px solid #e0e2e7; margin-top: 4px; padding-top: 10px; color: #6b7280; }
        .td-date-calendar { flex: 1; padding: 10px 16px; }
        .td-date-cal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; color: #1f2937; }
        .td-cal-nav {
          cursor: pointer; color: #6b7280; padding: 2px 8px; font-size: 16px;
          border-radius: 4px; transition: all 0.2s;
        }
        .td-cal-nav:hover { color: #111827; background: #eef1f5; }
        .td-date-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; text-align: center; }
        .td-date-cal-dow { color: #6b7280; font-size: 11px; margin-bottom: 4px; padding: 4px 0; }
        .td-cal-cell {
          padding: 6px 0; border-radius: 6px; cursor: pointer; font-size: 13px;
          color: #1f2937; transition: all 0.15s;
        }
        .td-cal-cell:hover { background: #eef1f5; }
        .td-cal-cell-empty { cursor: default; }
        .td-cal-cell-empty:hover { background: transparent; }
        .td-cal-cell-today { color: #b8860b; font-weight: 700; border: 1px solid #f1c232; }
        .td-cal-cell-selected { background: #4592f0; color: #fff; border-radius: 50%; }
        .td-cal-cell-disabled {
          opacity: 0.35;
          cursor: not-allowed;
          pointer-events: none;
        }

        .td-track-btn {
          background: transparent; border: 1px solid #e0e2e7; color: #1f2937; font-size: 12px;
        }
        .td-divider { border-color: #e8e9ec; }

        /* ReactQuill dark theme */
        .td-quill .ql-toolbar {
          background: #f4f5f7; border-color: #e0e2e7; border-radius: 6px 6px 0 0;
        }
        // .td-quill .ql-container {
        //   background: #f4f5f7; border-color: #e0e2e7; border-radius: 0 0 6px 6px;
        //   font-size: 13px; color: #1f2937; min-height: 100px;
        // }
        .td-quill .ql-editor.ql-blank::before { color: #8a8f98; font-style: normal; }
        .td-quill .ql-stroke { stroke: #6b7280; }
        .td-quill .ql-fill { fill: #6b7280; }
        .td-quill .ql-picker { color: #6b7280; }
        .td-quill-comment .ql-container { min-height: 70px; }

        .td-action-links {
          display: flex; flex-direction: column; gap: 10px; color: #6b7280;
          font-size: 13px; padding-bottom: 20px;
        }
        .td-action-links > div, .td-action-links-toggle { cursor: pointer; }
        .td-action-links > div:hover, .td-action-links-toggle:hover { color: #111827; }
        .td-action-links-toggle::after { display: none !important; }
        .td-upload-menu {
          background: #f4f5f7; border: 1px solid #e0e2e7; font-size: 13px;
        }
        .td-upload-menu .dropdown-item { color: #1f2937; }
        .td-upload-menu .dropdown-item:hover { background: #eef1f5; color: #111827; }

        .td-dropzone {
          border: 1px dashed #e0e2e7; border-radius: 6px; padding: 24px;
          text-align: center; font-size: 13px; color: #6b7280; background: #f8f9fb;
        }
        .td-dropzone-active { border-color: #4592f0; background: #eaf2ff; }
        .td-browse-link { color: #4592f0; cursor: pointer; text-decoration: underline; }
        .td-attachment-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 12px;
        }
        .td-attachment-card {
          position: relative; background: #f4f5f7; border: 1px solid #e0e2e7;
          border-radius: 6px; padding: 8px; text-align: center;
        }
        .td-attachment-thumb {
          width: 100%; height: 80px; object-fit: cover; border-radius: 4px; background: #ffffff;
        }
        .td-attachment-file-icon {
          display: flex; align-items: center; justify-content: center; color: #6b7280;
        }
        .td-attachment-name {
          font-size: 11px; color: #374151; margin-top: 6px; white-space: nowrap;
          overflow: hidden; text-overflow: ellipsis;
        }
        .td-attachment-remove {
          position: absolute; top: 4px; right: 4px; background: rgba(0,0,0,0.6);
          border: none; color: #fff; border-radius: 50%; width: 20px; height: 20px;
          display: flex; align-items: center; justify-content: center; z-index: 2;
        }

        .td-activity {
          border-left: 1px solid #e8e9ec; height: 100%; display: flex;
          flex-direction: column; overflow: hidden;
        }
        .td-comments-list { overflow-y: auto; flex: 1; }
        .td-comment { display: flex; gap: 8px; margin-bottom: 14px; }
        .td-avatar {
          width: 26px; height: 26px; border-radius: 50%; background: #ece9fb;
          color: #5b4fb8; font-size: 10px; font-weight: 700; display: flex;
          align-items: center; justify-content: center; flex-shrink: 0;
        }
        /* Comments always sit below the activity log, clearly separated,
           and each comment's text renders inside its own ClickUp-style box */
        .td-comments-section {
          margin-top: 10px; padding-top: 12px; border-top: 1px solid #e8e9ec;
        }
        // .td-comment-text {
        //   // color: #374151; border: 1px solid #e0e2e7; border-radius: 8px;
        //   // background: #ffffff; padding: 8px 10px; margin-top: 4px;
        // }
        .td-comment-box { flex-shrink: 0; }
        .td-send-btn { display: flex; align-items: center; justify-content: center; }
        .td-error { color: #f2394a; font-size: 11px; margin-top: 4px; }

        .td-avatar-img { border-radius: 50%; object-fit: cover; flex-shrink: 0; }
        .td-activity-line { margin-bottom: 14px; }
        .td-activity-user { color: #374151; font-weight: 600; }
        .td-activity-attachment {
          display: flex; align-items: center; gap: 8px; margin-top: 6px;
          background: #f4f5f7; border: 1px solid #e0e2e7; border-radius: 6px;
          padding: 6px 8px; max-width: 220px;
        }
        .td-activity-attachment img {
          width: 40px; height: 40px; object-fit: cover; border-radius: 4px; flex-shrink: 0;
        }
        .td-activity-attachment-file {
          width: 40px; height: 40px; display: flex; align-items: center;
          justify-content: center; background: #ffffff; border-radius: 4px;
          color: #6b7280; flex-shrink: 0;
        }
        .td-comment-actions {
          display: flex; align-items: center; gap: 10px; margin-top: 6px; font-size: 13px;
        }
        .td-comment-action { cursor: pointer; opacity: 0.7; }
        .td-comment-action:hover { opacity: 1; }
        .td-comment-reply { color: #6b7280; cursor: pointer; margin-left: auto; font-size: 12px; }
        .td-comment-reply:hover { color: #111827; }

        /* ---- TaskCommentEditor (custom, replaces react-quill) ---- */
        .td-comment-editor {
          border: 1px solid #e0e2e7;
          border-radius: 8px;
          overflow: hidden;
          background: #ffffff;
        }
        .td-comment-files {
          display: flex; flex-wrap: wrap; gap: 6px; padding: 6px 10px 0;
        }
        .td-comment-file-chip {
          background: #f0f1f4; border: 1px solid #e0e2e7; border-radius: 4px;
          font-size: 11px; padding: 2px 8px; display: inline-flex;
          align-items: center; gap: 4px;
        }
        .td-comment-format-bar {
          display: flex; gap: 12px; padding: 6px 10px;
          border-bottom: 1px solid #e0e2e7; background: #f8f9fb; color: #6b7280;
        }
        .td-comment-format-bar span {
          cursor: pointer; padding: 2px 4px; border-radius: 4px; transition: all 0.2s;
        }
        .td-comment-format-bar span:hover { color: #111827; background: #f0f1f4; }
        .td-comment-input {
          min-height: 70px; padding: 10px; font-size: 13px; color: #1f2937;
          outline: none; background: #ffffff;
        }
        .td-comment-input:empty::before { content: attr(data-placeholder); color: #8a8f98; }
        .td-comment-toolbar {
          display: flex; align-items: center; gap: 10px; padding: 6px 10px;
          border-top: 1px solid #e0e2e7; background: #f8f9fb;
        }
        .td-comment-tool {
          color: #6b7280; cursor: pointer; display: inline-flex; align-items: center;
          padding: 4px 6px; border-radius: 4px; transition: all 0.2s;
        }
        .td-comment-tool:hover { color: #111827; background: #f0f1f4; }
        .td-comment-tool-label {
          font-size: 12px; gap: 4px; padding: 4px 10px;
          border: 1px solid #e0e2e7; border-radius: 4px;
        }
        .td-comment-tool-label:hover { border-color: #c7cbd1; }
        .td-comment-send {
          margin-left: auto; background: #4592f0; border: none; color: #fff;
          border-radius: 6px; width: 28px; height: 28px; display: flex;
          align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s;
        }
        .td-comment-send:hover { background: #3b82f6; transform: scale(1.05); }
        .td-comment-send:disabled { background: #e8e9ec; color: #8a8f98; cursor: not-allowed; transform: none; }

        .td-comment-mention-popover, .td-comment-emoji-popover {
          background: #ffffff;
        }
        .td-comment-mention-popover {
          min-width: 200px; max-height: 220px; overflow-y: auto;
        }
        .td-comment-mention-row {
          display: flex; align-items: center; gap: 8px; padding: 6px 8px;
          border-radius: 6px; cursor: pointer;
        }
        .td-comment-mention-row:hover { background: #eef1f5; }
        .td-comment-mention-empty { padding: 8px; font-size: 12px; color: #8a8f98; }
        .td-comment-emoji-popover {
          display: grid; grid-template-columns: repeat(6, 1fr); gap: 4px; width: 200px;
        }
        .td-comment-emoji-item {
          cursor: pointer; font-size: 18px; text-align: center; padding: 4px;
          border-radius: 4px; transition: background 0.2s;
        }
        .td-comment-emoji-item:hover { background: #eef1f5; }

        /* ---- Status pill + confirm modal (same as Projecttasklistview) ---- */
        .tl-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
        }
        .tl-status-pill {
          border: none;
          border-radius: 4px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.3px;
          color: #ffffff;
          padding: 4px 10px 4px 8px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s;
          white-space: nowrap;
        }
        .tl-status-pill:hover { filter: brightness(1.08); }
        .tl-status-pill-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: rgba(255,255,255,0.9);
          box-shadow: 0 0 0 2px rgba(255,255,255,0.35);
          display: inline-block;
          flex-shrink: 0;
        }
        .tl-option-popover { min-width: 160px; }
        .tl-popover-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 8px;
          border-radius: 6px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .tl-popover-row:hover { background: #f0f0f0; }
        .tl-status-modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(20, 20, 24, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .tl-status-modal {
          width: 420px;
          max-width: 92vw;
          background: #fff;
          border-radius: 10px;
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }
        .tl-status-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          font-size: 15px;
          font-weight: 600;
          color: #1a1a1e;
          border-bottom: 1px solid #eee;
        }
        .tl-status-modal-close {
          cursor: pointer;
          color: #888;
          display: inline-flex;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s;
        }
        .tl-status-modal-close:hover {
          color: #1a1a1e;
          background: #f0f0f0;
        }
        .tl-status-modal-body {
          padding: 18px 20px 6px;
        }
        .tl-status-modal-message {
          margin: 0 0 12px;
          font-size: 13px;
          color: #1a1a1e;
        }
        .tl-status-modal-label {
          display: block;
          font-size: 12px;
          color: #666;
          margin-bottom: 6px;
        }
        .tl-status-modal-textarea {
          width: 100%;
          min-height: 90px;
          resize: vertical;
          border: 1px solid #ccc;
          border-radius: 6px;
          padding: 10px;
          font-size: 13px;
          color: #1a1a1e;
          font-family: inherit;
        }
        .tl-status-modal-textarea:focus {
          outline: none;
          border-color: #7B3FE4;
        }
        .tl-status-modal-textarea::placeholder { color: #999; }
        .tl-status-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          padding: 16px 20px;
        }
        .tl-btn-cancel {
          font-size: 12px;
          border-radius: 6px;
          padding: 5px 14px;
          border: 1px solid #ccc;
          background: transparent;
          color: #1a1a1e;
          cursor: pointer;
          transition: all 0.2s;
        }
        .tl-btn-cancel:hover {
          background: #f0f0f0;
          border-color: #aaa;
        }
        .tl-status-modal-submit {
          font-size: 12px;
          border-radius: 6px;
          padding: 5px 16px;
          border: 1px solid #a13b58;
          background: #a13b58;
          color: #fff;
          cursor: pointer;
          transition: all 0.2s;
        }
        .tl-status-modal-submit:hover {
          background: #8c3249;
        }
        .tl-status-modal-submit:disabled,
        .tl-btn-cancel:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </Modal>
  );
};

export default TaskDetailModal;